
import {getDocs,orderBy,where,query} from 'firebase/firestore'
import { Component, Prop, Watch,Mixins} from 'vue-property-decorator';
import { ExUserPublicProfile } from "@/scripts/datatypes"
import { ClassData, GetUserRolesAccessLevelsRes, MemberMeta, Role, Roles, UserPublicProfile } from "../common-datatypes"
import Common from '@/mixins/common'
import {toHiragana,toZen} from 'jaconv'
import TemplateMembers from '@/components/TemplateMembers.vue'

type Taxonomy = {
	[key in 'class' | 'group' | 'role']: Term[]
}


interface Term {
  name: string;
  id: string;
  count: number;
}

interface SelectedFilter {
  classId: string;
  groupId: string;
  role: Role | '';
  excludeUserIds: string[];
  searchText: string;
}

@Component({
  components: {
    TemplateMembers
  }})

export default class ModalSelectUser extends Mixins(Common)  {
  @Prop({type: Array,default: () => {return []}}) private selectedUserIds?: string[]
  @Prop({type: String, default: ''}) private title?: string
  @Prop({type: Array,default: () => {return []}}) private excludeUserIds?: string[]
  @Prop({type: Array,default: () => {return []}}) private includeUserIds?: string[]
  @Prop({type: Array,default: () => {return []}}) private excludeRoles?: Roles
  @Prop({type: Array,default: () => {return []}}) private includeRoles?: Roles
  @Prop({type: Object,default: () => {return {}}}) private memberMeta?: {[key: string]: MemberMeta}
  @Prop({type: Boolean, default: true}) private isShow?: boolean

  isLoading = true

  isActive = true

  orderBy: keyof UserPublicProfile = 'creationTime'

  users: ExUserPublicProfile[] = []

  userRolesAccessLevels?: GetUserRolesAccessLevelsRes

  cls: ClassData[] = []

  taxonomy: Taxonomy = {
    class: [],
    group: [],
    role: [],
  }

  selectedFilter: SelectedFilter = {
    classId: '',
    groupId: '',
    role: 'student',
    excludeUserIds: [],
    searchText: '' 
  }

  get userIds(){
    return this.selectedUserIds || []
  }

  set userIds(users: string[]){
    this.$emit('update:selectedUserIds', users)
  }

  get userMeta(){
    return this.memberMeta || {}
  }

  set userMeta(userMeta: {[key: string]: MemberMeta}){
    this.$emit('update:memberMeta', userMeta)
  }

  @Watch('selectedUserIds',{immediate: true})
  updateSelectedUserIds(newVal: string[]){
    this.selectedFilter.excludeUserIds = Array.from(new Set(newVal.concat(this.excludeUserIds || [])))
  }

  @Watch('excludeUserIds',{immediate: true})
  updateExcludeUserIds(newVal: string[]){
    this.selectedFilter.excludeUserIds = Array.from(new Set(newVal.concat(this.selectedUserIds || [])))
  }

  get roles(): string[]{
    const roles = Object.keys(this.userRolesAccessLevels || {})
    return roles
  }

  setFilterOnUsersData(filter: SelectedFilter){
    const searchText = toHiragana(toZen(filter.searchText))

    const settingFilter = !! (filter.classId || filter.groupId || filter.excludeUserIds || filter.role || filter.searchText)

    this.users = this.users.map(user => {
      user.hidden = false

      if(! settingFilter){
        return user
      }

      if(filter.excludeUserIds.length > 0 && filter.excludeUserIds.includes(user.userId || '')){
        user.hidden = true
        return user
      }

      if(searchText && ! this.$findTextByUserProfile(searchText, user)){
        user.hidden = true
        return user
      }

      if(filter.classId && ! this.cls.some((cl)=>{return cl.classId === filter.classId && user.userId && cl.memberUserIds && cl.memberUserIds.includes(user.userId)})){
        user.hidden = true
        return user
      }

      if(filter.role && ! user.roles?.includes(filter.role)){
        user.hidden = true
        return user
      }

      return user
    })
  }

  @Watch('selectedFilter',{deep: true})
  changeSelectedFilter(newVal: SelectedFilter){
    this.setFilterOnUsersData(newVal)
  }

  setTaxonomies(){
    this.taxonomy.role = Object.keys(this.userRolesAccessLevels || {}).map((role) => {
      return {name: role, id: role, count: 0}
    })

    this.taxonomy.class = this.cls.map((cl) => {
      return {name: cl.name || '', id: cl.classId || '', count: 0}
    })

    this.users.forEach(user => {
      if(user.roles){
        user.roles.forEach((role)=>{this.setTaxonomy(this.taxonomy.role ,role)})
      }
    })

    this.taxonomy.role = this.taxonomy.role.filter((term)=>{
      if(this.selectedFilter.role === term.name && term.count === 0){
        this.selectedFilter.role = ''
      }
      return term.count > 0
    })
  }

  setTaxonomy(type: Term[], name: string, id = ''){
    if(! name){
      return
    }

    const findIndex = type.findIndex((term) => {
      if(id !== ''){
        return term.id === id && term.name === name
      }
      return term.name === name
    })

    const term: Term = {
      name: name,
      count: 1,
      id: id
    }

    if(findIndex === -1){
      type.push(term)
    }else{
      term.count = 1 + type[findIndex].count
      type.splice(findIndex, 1,term)
    }
  }

  async created(){
    try{
      const [userPublicProfileDocs,userClassesDocs,userRolesAccessLevels] = await Promise.all([
        getDocs(query(this.$getUsersPublicProfileQuery(),where('deleted', '==',false),where('disabled', '==',false),orderBy(this.orderBy))),
        getDocs(this.$store.getters.isStaff ? query(this.$getClassesColRef(),where('deleted','==',false),where('disabled','==',false)) : this.$getUserClassesQuery()),
        this.$getUserRolesAccessLevels(this.$store.getters.schoolId)
      ])

      const allUsers = userPublicProfileDocs.docs.map((doc) => {
        const data = doc.data() as UserPublicProfile
        return {
          ...data,
          hidden: true,
        }
      })

      let users: ExUserPublicProfile[]  = []

      if((this.includeUserIds && this.includeUserIds.length > 0) || (this.includeRoles && this.includeRoles.length > 0)){
        if(this.includeRoles && this.includeRoles.length > 0){
          users = users.concat(allUsers.filter((user) => {
            return (user.userId && this.includeUserIds?.includes(user.userId)) || (user.roles && user.roles.some((role) => {return this.includeRoles && this.includeRoles.includes(role)}))
          }))
        }
      }else{
        users = allUsers
      }
      
      if(this.excludeRoles && this.excludeRoles.length > 0){
        users = users.filter((user) => {
          return user.roles && ! user.roles.some((role) => {return this.excludeRoles && this.excludeRoles.includes(role)})
        })
      }
      
      this.users = users.map((user) => {
        return {
          ...user,
          disabled: true
        }
      })

      this.userRolesAccessLevels = userRolesAccessLevels.data

      this.cls = userClassesDocs.docs.map((doc) => {
          const row = doc.data()
          return {classId: doc.id,...row} as ClassData
      })

      this.setTaxonomies()
      this.setFilterOnUsersData(this.selectedFilter)

    }catch(error){
      //console.table(error)
    }finally{
      this.isLoading = false
    }
  }

  mounted(){
    //
  }

  selectUser(user: ExUserPublicProfile){
    const userId = user.userId || ''

    if(! userId){
      return
    }

    const userIds = [...(this.selectedUserIds || [])]
    const meta = {...(this.memberMeta || {})}

    if(! userIds.includes(userId)){
      userIds.push(userId)
    }

    meta[userId as keyof typeof meta] = {
      ref: this.$getUserPublicProfileDocRef(userId),
      lastName: user.lastName || '',
      firstName: user.firstName || '',
      lastNameYomigana: user.lastNameYomigana || '',
      firstNameYomigana: user.firstNameYomigana || ''
    }

    this.$emit('update:selectedUserIds', userIds)
    this.$emit('update:memberMeta', meta)
  }

}
